import React, { useEffect, useState, useRef, useMemo } from "react";

import cx from "components/src/utils/cx";

// Types
import type {
  MarketButtonProps,
  PrevOddsType,
  MarketButtonArrowAnimationOptions,
  MarketButtonVariantType,
} from "./types";

// Helper JS
import parseOdds from "./js/parseOdds";
// import { animationOptions } from "./js/animationOptions";

// Hooks
import useOddsState from "./hooks/useOddsState";
import { useSync } from "shared/src/hooks/useSync";

// import { useIntersectionObserver } from "shared/src/hooks/useIntersectionObserver";
// import { useNumberTweening } from "./hooks/useNumberTweening";

// Components
import ArrowIndicators from "./components/ArrowIndicators";
import Lock from "./components/Lock";
import Line from "./components/Line";
import MBNotification from "./components/MBNotification/MBNotification";
import Odds from "./components/Odds";
import { Loading } from "components/src/components/Loading/Loading";
import { Placeholder } from "./components/Placeholder";
import MarketButtonWrapper from "./components/MarketButtonWrapper";
import OddsContainer from "./components/OddsContainer";
import Description from "./components/Description";
import { PromoText } from "./components/PromoText";
import { Explainer } from "./components/Explainer";
import { Was } from "./components/Was/Was";
import { Icon } from "components/src/components/Icon/Icon";
import { Badge } from "components/src/components/Badge/Badge";

// Styles
import { BUTTON_STATES } from "./styles/styles";
import { makeAriaLabel, overUnderArr } from "./js/makeAriaLabel";

const onClickDefault = () => {};

export const MarketButton = (props: MarketButtonProps) => {

  /**
   *  useSync hook
   */
  const propsWithSync = useSync<MarketButtonProps>(props);
  /** ----------------------------------------------------------------- */

  const {
    id,
    odds: oddsProp = "",
    line = "",
    lineLabel,
    isSelected: isSelectedProp = false,
    isSelectedBuilder,
    isLoading = false,
    isDisabled = false,
    isInactive = false,
    isMuted = false,
    isStrikethrough = false,
    isPlaceholder = false,
    variant = "fixed",
    rounded,
    animation = "default",
    description,
    icon,
    fluid,
    fullHeight,
    promoText,
    was,
    tabbable = true,
    metadata = {},
    onClick = onClickDefault,
    price,
    priceDisplayFormatter,
    showAddedNotification = false,
    truncateDescription = false,
    placeholderType = "default",
    dataQa,
    filterGroup,
    ...rest
  } = propsWithSync;
  const odds = price && priceDisplayFormatter ? priceDisplayFormatter(price) : oddsProp;
  const isSelected = isSelectedBuilder ? isSelectedBuilder(id) : isSelectedProp;

  const [selected, setSelected] = useState<boolean>(isSelected);
  const [focused, setFocused] = useState<boolean>(false);
  const [explainerText, setExplainerText] = useState<string>('');
  const parsedOdds = odds ? parseOdds(odds) : parseOdds("EVS");

  const [animationState, setAnimationState] =
    useState<MarketButtonArrowAnimationOptions>(animation);

  const effectiveAnimation = isInactive ? "off" : animationState;

  const [oddsState] = useOddsState({
    odds: parsedOdds.exactOdds,
    oddsType: parsedOdds.type,
    // delay: animationOptions.blinkDuration[animationState],
  });

  // const oddsRef = useRef<HTMLSpanElement>(null);
  // const lineRef = useRef<HTMLSpanElement>(null);
  const prevOdds = useRef<PrevOddsType>({
    parsedOdds: parsedOdds,
    line,
  });
  const prevBetSlipState = useRef<boolean>(isSelected);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const ODDS_STATE = useMemo(() => {
    if (isInactive) return { UP: false, DOWN: false, EVEN: true };
    return {
      UP: oddsState === "UP",
      DOWN: oddsState === "DOWN",
      EVEN: oddsState === "EVEN",
    };
  }, [oddsState, isInactive]);

  // Flags
  const _variant: MarketButtonVariantType =
    variant.toLowerCase() as MarketButtonVariantType;
  const [isBoost, isPromo, isHighlight, isOverUnder] = [
    _variant === "boost",
    _variant === "promo",
    _variant === "highlight",
    !!lineLabel && overUnderArr.includes(lineLabel.toLowerCase()),
  ];
  const isDisabledSelected = isDisabled && isSelected;
  const isEnabled =
    (!isLoading && !isDisabled && !isStrikethrough && !isPlaceholder) || isDisabledSelected;
  let isLargeOdds: boolean = false;
  if (odds !== null) {
    isLargeOdds =
      typeof odds === "string" ? odds.length > 6 : odds.toString().length > 6;
  }
  const isVariantLarge = _variant === "large";

  const roundedClass = rounded ? { sm: "cui-rounded-sm", md: "cui-rounded-md", lg: "cui-rounded-lg" }[rounded] : "cui-rounded-2px";

  // Aria Label
  const ariaLabel = useMemo(() => makeAriaLabel(description, isBoost, isPromo, was, lineLabel, isOverUnder, line, odds, explainerText, metadata), [description, isBoost, isPromo, was, lineLabel, isOverUnder, line, odds, explainerText, metadata]);

  const isBase = !selected && isEnabled && !isVariantLarge;

  const marketButtonClasses = cx(
    {
      [BUTTON_STATES.BASE]: isBase && !isInactive && !isMuted,
      [BUTTON_STATES.BASE_LARGE]:
        !selected && isEnabled && isVariantLarge,
      [BUTTON_STATES.FOCUSED]: true,
      [BUTTON_STATES.HIGHLIGHT]:
        variant === "highlight" && !selected && isEnabled,
       [BUTTON_STATES.INACTIVE]:
        variant !== "highlight" && !selected && isEnabled && isInactive,
       [BUTTON_STATES.MUTED]:
        variant !== "highlight" && !selected && isEnabled && isMuted,
      [BUTTON_STATES.STRIKETHROUGH]: isStrikethrough,
      [BUTTON_STATES.PLACEHOLDER]: isPlaceholder || odds === null,
      [BUTTON_STATES.LOADING]: isLoading,
      [BUTTON_STATES.DISABLED]: isDisabled && !isDisabledSelected,
      [BUTTON_STATES.DISABLED_SELECTED]: isDisabledSelected, 
      [`${BUTTON_STATES.BASE} cui__market-button-odds-states`]:
        ODDS_STATE.UP || ODDS_STATE.DOWN,
      [BUTTON_STATES.SELECTED]: selected && isEnabled && !isVariantLarge && !isDisabledSelected,
      [BUTTON_STATES.SELECTED_LARGE]:
          selected && isEnabled && isVariantLarge && !isDisabledSelected,
        "cui-h-full": !isVariantLarge,
      [BUTTON_STATES.BOOSTED]: isBoost && !selected,
      "cui-py-sm cui-min-h-[144px]": isVariantLarge,
      "hover:cui-bg-bg-layer-two": isBase && !isDisabledSelected && !isInactive && !isMuted,
      "cui-transition-colors cui-transition-opacity cui-transition-transform cui-duration-200": animation !== "off",
      "cui-border-fg-primary-minimal": isPlaceholder && placeholderType !== "default",
      "cui-border-fg-minimal": isPlaceholder && placeholderType === "default"
    },
    `cui__market-button cui-z-0 ${roundedClass} cui-outline-none cui-box-border cui-border-1 cui-relative cui-w-full cui-p-0 cui-m-0 cui-inline-flex cui-flex-col cui-justify-center cui-items-center cui-relative cui-cursor-pointer cui-select-none`
  );

  /* Handlers */

  const handleFocus = () => {
    setFocused((val) => !val);
  };

  /* Hooks */

  // const io = useIntersectionObserver({
  //   refs: [wrapperRef],
  //   onVisible(entry) {
  //     setAnimationState(animation);
  //   },
  //   onInvisible(entry) {
  //     setAnimationState("off");
  //   },
  // });

  useEffect(() => {
    setSelected(isSelected);
    prevBetSlipState.current = isSelected;
    // setAnimationState(animation);
  }, [isSelected]); // removed animation

  // useNumberTweening({
  //   prevOdds,
  //   parseOdds,
  //   line,
  //   odds,
  //   lineRef,
  //   oddsRef,
  //   animationState,
  //   parsedOdds,
  //   animationOptions,
  //   description,
  //   isEnabled,
  // });

  if (odds === null) {
    return (
      <MarketButtonWrapper
        variant={_variant}
        isEnabled={false}
        hasDescription={false}
        fluid={fluid}
        rounded={rounded}
        dataQa={dataQa}
        fullHeight={fullHeight}
        filterGroup={filterGroup}
      >
        <div className={marketButtonClasses}>
          <Placeholder placeholderType={placeholderType} />
        </div>
      </MarketButtonWrapper>
    );
  }

  // All the non-enabled versions of this button, as a DIV to pass accessibility.
  if (!isEnabled) {
    return (
      <MarketButtonWrapper
        variant={_variant}
        isEnabled={isEnabled}
        ref={wrapperRef}
        hasDescription={!!description}
        fluid={fluid}
        fullHeight={fullHeight}
        rounded={rounded}
        dataQa={dataQa}
        filterGroup={filterGroup}
      >
        <div className={marketButtonClasses}>
          {isLoading && <Loading size="sm" type="dots" animation={true} />}
          {isDisabled && !isLoading && (
            <Lock
              className={cx(
                {
                  "cui-w-10 cui-h-10": isVariantLarge,
                },
                "cui-fill-fg-bold"
              )}
              data-cy="market-button-lock"
            />
          )}
          {isPlaceholder && !isLoading && !isDisabled && <Placeholder placeholderType={placeholderType} />}
          {isStrikethrough && !isLoading && !isDisabled && !isPlaceholder ? (
            <div
              className="cui-flex cui-flex-col cui-w-full"
              data-cy="market-button-strikethrough"
            >
              <div className="cui-line-through cui-text-fg-moderate cui-mx-auto">
                {odds}
              </div>
            </div>
          ) : null}
        </div>
      </MarketButtonWrapper>
    );
  }
  // Market Button
  return (
    <MarketButtonWrapper
      variant={_variant}
      isEnabled={isEnabled}
      ref={wrapperRef}
      hasDescription={!!description}
      fluid={fluid}
      fullHeight={fullHeight}
      rounded={rounded}
      dataQa={dataQa}
      filterGroup={filterGroup}
    >
      {!isLoading && animationState !== "off" && showAddedNotification ? (
        <MBNotification
          isSelected={isSelected}
          prevState={prevBetSlipState.current}
          variant={_variant}
        />
      ) : null}
      <button
        id={`market-button-${id}`}
        type="button"
        role="switch"
        tabIndex={tabbable ? 0 : -1}
        aria-checked={selected}
        aria-disabled={isDisabled && !isDisabledSelected}
        aria-label={ariaLabel}
        disabled={isDisabled && !isDisabledSelected}
        className={marketButtonClasses}
        data-cy="market-button-btn"
        data-qa={dataQa?.marketButton}
        {...rest}
        onClick={(e) => onClick(e, id)}
        onFocus={handleFocus}
        onBlur={handleFocus}
      >
        { isDisabledSelected ? <>
          <Lock
              className={cx(
                {
                  "cui-w-18 cui-h-18 cui-mb-sm": isVariantLarge,
                },
                "cui-fill-fg-bold cui-mb-2px"
              )}
              data-cy="market-button-lock"
            />
          <Badge text="Remove" status="negative" type="subtle" shape="square" />
          </> : null}
        { !isDisabledSelected ? <>
          <ArrowIndicators
            oddsState={ODDS_STATE}
            selected={selected}
            isBoost={isBoost}
            id={id}
            isViewable={parsedOdds.type === prevOdds.current.parsedOdds.type}
            animation={effectiveAnimation}
            dataQa={dataQa}
            rounded={rounded}
          />

          {!description ? (
            <Line
              variant={_variant}
              // lineRef={lineRef}
              selected={selected}
              lineLabel={lineLabel}
              hasLine={!!line}
              line={line}
              isInactive={isInactive}
              isMuted={isMuted}
            />
          ) : (
            <Description
              isVariantLarge={isVariantLarge}
              isSelected={isSelected}
              selected={selected}
              description={description}
              line={line}
              lineLabel={lineLabel}
              // lineRef={lineRef}
              variant={_variant}
              icon={icon}
              truncateDescription={truncateDescription}
            />
          )}
          <OddsContainer variant={_variant} truncateDescription={truncateDescription} hasDescription={!!description}>
            {isBoost ? <Icon name="ui_boost" color={selected ? "fg-inverse" : "fg-global-black"} /> : null}
            <Odds
              // oddsRef={oddsRef}
              selected={selected}
              // oddsState={ODDS_STATE.DOWN}
              isLargeOdds={isLargeOdds}
              variant={_variant}
              odds={odds}
              isInactive={isInactive}
              isMuted={isMuted}
            />
            <PromoText
              promoText={promoText}
              variant={_variant}
              odds={odds}
              selected={selected}
              setExplainerText={setExplainerText}
            />
          </OddsContainer>
        </> : null }
      </button>
    </MarketButtonWrapper>
  );
};

MarketButton.Explainer = Explainer;

export default MarketButton;
